import { InlineMarkdown } from '@/components/core/markdown/markdown';
import { cn } from '@/lib/utils';
import { splitAtMidpoint } from './splitTitle';

// Inline or display maths: `$...$`, `$$...$$`, `\(...\)` or `\[...\]`.
const MATH = /\$[^$]+\$|\\\(|\\\[/;

/**
 * Whether a model-written title carries maths. Templates that split a title
 * into words or halves skip the split when it does: a split can cut through
 * `$\frac{a}{b}$` and leave both halves unparseable.
 */
export function hasMath(text: string): boolean {
  return MATH.test(text);
}

/**
 * Model-written slide text (a title, half a title, the concept caption)
 * rendered with its maths. The templates set titles `uppercase`, which
 * would turn the variables in `$x_1$` into `X_1`, so KaTeX opts out.
 */
export function SlideText({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  return (
    <InlineMarkdown className={cn('[&_.katex]:normal-case', className)}>
      {text}
    </InlineMarkdown>
  );
}

/**
 * A title split at its midpoint into two colours (`tones`), each half its
 * own heading. A title with maths renders whole in the first tone:
 * splitting could cut through the expression.
 */
export function TwoToneTitle({
  title,
  as: Tag,
  className,
  gap,
  tones,
}: {
  title: string;
  as: 'h1' | 'h3';
  className: string;
  /** Spacing class on the second half. */
  gap?: string;
  tones: [string, string];
}) {
  const math = hasMath(title);
  const { first, second } = math
    ? { first: title, second: '' }
    : splitAtMidpoint(title);
  return (
    <>
      <Tag className={className} style={{ color: tones[0] }}>
        {math ? <SlideText text={first} /> : first}
      </Tag>
      {second && (
        <Tag className={cn(gap, className)} style={{ color: tones[1] }}>
          {second}
        </Tag>
      )}
    </>
  );
}
